import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, Trash2, Ruler, AlertTriangle, ChevronDown, ChevronRight, Cable } from 'lucide-react';
import { WireType } from './WireTypeModal';
import '../styles/WireRunsPanel.css';

interface Point {
  x: number;
  y: number;
}

export interface WireRun {
  id: string;
  estimation_id: string;
  file_id: string;
  wire_type: WireType;
  points: Point[];
  page_number: number;
  total_length: number;
  segments: number;
  created_at: string;
}

interface WireRunsPanelProps {
  estimationId: string;
  fileId?: string;
  hasCalibration: boolean;
  onStartWireRun: () => void;
  onOpenCalibration: () => void;
  onHighlightRun?: (runId: string | null) => void;
  onWireRunDeleted?: (runId: string, pageNumber: number) => void;
  refreshTrigger?: number;
}

export const WireRunsPanel: React.FC<WireRunsPanelProps> = ({
  estimationId,
  fileId,
  hasCalibration,
  onStartWireRun,
  onOpenCalibration,
  onHighlightRun,
  onWireRunDeleted,
  refreshTrigger
}) => {
  const [wireRuns, setWireRuns] = useState<WireRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());

  // Load wire runs from database
  const loadWireRuns = useCallback(async () => {
    if (!estimationId) return;

    try {
      setLoading(true);
      let query = supabase
        .from('wire_runs')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('created_at', { ascending: false });

      // Optionally filter by file
      if (fileId) {
        query = query.eq('file_id', fileId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error loading wire runs:', error);
        return;
      }

      setWireRuns(data || []);
      
      // Auto-expand all types on first load
      if (data && data.length > 0) {
        const types = new Set(data.map(r => r.wire_type.name));
        setExpandedTypes(types);
      }
    } catch (err) {
      console.error('Error loading wire runs:', err);
    } finally {
      setLoading(false);
    }
  }, [estimationId, fileId]);

  useEffect(() => {
    loadWireRuns();
  }, [loadWireRuns, refreshTrigger]);

  // Delete a wire run
  const deleteWireRun = async (runId: string, pageNumber: number) => {
    try {
      const { error } = await supabase
        .from('wire_runs')
        .delete()
        .eq('id', runId);

      if (error) {
        console.error('Error deleting wire run:', error);
        return;
      }

      setWireRuns(prev => prev.filter(r => r.id !== runId));
      
      // Also delete the corresponding annotation
      if (onWireRunDeleted) {
        onWireRunDeleted(runId, pageNumber);
      }
    } catch (err) {
      console.error('Error deleting wire run:', err);
    }
  };

  // Group runs by wire type
  const groupedRuns = wireRuns.reduce((acc, run) => {
    const typeName = run.wire_type.name;
    if (!acc[typeName]) {
      acc[typeName] = {
        wireType: run.wire_type,
        runs: [],
        totalLength: 0
      };
    }
    acc[typeName].runs.push(run);
    acc[typeName].totalLength += run.total_length;
    return acc;
  }, {} as Record<string, { wireType: WireType; runs: WireRun[]; totalLength: number }>);

  // Calculate grand total
  const grandTotal = wireRuns.reduce((sum, run) => sum + run.total_length, 0);

  const toggleExpand = (typeName: string) => {
    setExpandedTypes(prev => {
      const next = new Set(prev);
      if (next.has(typeName)) {
        next.delete(typeName);
      } else {
        next.add(typeName);
      }
      return next;
    });
  };

  return (
    <div className="wire-runs-panel">
      <div className="wire-runs-header">
        <h4>
          <Cable size={16} />
          Wire & Conduit Runs
        </h4>
        <button 
          className="new-run-btn"
          onClick={onStartWireRun}
          disabled={!hasCalibration}
          title={hasCalibration ? 'Start new wire run' : 'Calibrate scale first'}
        >
          <Plus size={14} />
          New Run
        </button>
      </div>

      {/* Calibration Warning */}
      {!hasCalibration && (
        <div className="calibration-prompt">
          <AlertTriangle size={16} />
          <div className="prompt-content">
            <span>Set scale for accurate measurements</span>
            <button onClick={onOpenCalibration}>
              <Ruler size={12} />
              Calibrate
            </button>
          </div>
        </div>
      )}

      {/* Instructions when empty */}
      {wireRuns.length === 0 && !loading && (
        <div className="empty-state">
          <Cable size={32} />
          <p>No wire runs yet</p>
          <span>Click "New Run" to start measuring wire and conduit runs</span>
          <div className="instructions">
            <strong>How to use:</strong>
            <ol>
              <li>Click to start a run</li>
              <li>Click to add points along the path</li>
              <li>Right-click to finish</li>
              <li>Select wire/conduit type</li>
            </ol>
          </div>
        </div>
      )}

      {/* Wire runs list grouped by type */}
      {Object.keys(groupedRuns).length > 0 && (
        <div className="wire-runs-list">
          {Object.entries(groupedRuns)
            .sort((a, b) => b[1].totalLength - a[1].totalLength)
            .map(([typeName, group]) => (
              <div key={typeName} className="wire-type-group">
                <div 
                  className="group-header"
                  onClick={() => toggleExpand(typeName)}
                >
                  <span className="expand-icon">
                    {expandedTypes.has(typeName) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                  <span 
                    className="color-dot" 
                    style={{ backgroundColor: group.wireType.color }}
                  />
                  <span className="type-name">{typeName}</span>
                  <span className="type-count">{group.runs.length} run{group.runs.length !== 1 ? 's' : ''}</span>
                  <span className="type-total">{group.totalLength.toFixed(1)} ft</span>
                </div>

                {expandedTypes.has(typeName) && (
                  <div className="runs-list">
                    {group.runs.map(run => (
                      <div 
                        key={run.id} 
                        className="run-item"
                        onMouseEnter={() => onHighlightRun?.(run.id)}
                        onMouseLeave={() => onHighlightRun?.(null)}
                      >
                        <span className="run-length">{run.total_length.toFixed(1)} ft</span>
                        <span className="run-segments">{run.segments} seg</span>
                        <span className="run-page">pg {run.page_number}</span>
                        <button 
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteWireRun(run.id, run.page_number);
                          }}
                          title="Delete run"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}

      {/* Summary */}
      {wireRuns.length > 0 && (
        <div className="wire-runs-summary">
          <div className="summary-row total">
            <span>Total All Runs</span>
            <span className="summary-value">{grandTotal.toFixed(1)} ft</span>
          </div>
          <div className="summary-breakdown">
            {Object.entries(
              wireRuns.reduce((acc, run) => {
                const cat = run.wire_type.category;
                acc[cat] = (acc[cat] || 0) + run.total_length;
                return acc;
              }, {} as Record<string, number>)
            ).map(([category, length]) => (
              <div key={category} className="summary-row">
                <span className="category-name">{category}</span>
                <span>{(length as number).toFixed(1)} ft</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-state">
          Loading wire runs...
        </div>
      )}
    </div>
  );
};

export default WireRunsPanel;

